import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getWithParams } from '../api';
import { mapToMlLevel } from '../Jacques';
import {
    getFuturePotentialCategory,
    projectLevelByAge,
} from '../utils/employees';
import { DEFAULT_POTENTIAL_RULESET } from '../utils/potentialDSL';
import { withLanguage } from '../components/common/Texts';
import { getJacquesPotential } from '../utils/potential';
import { sublevelToDomainLevel } from '../utils/jacquesGeometry';

function getCoordsWide2(age, ageProportion, level) {
    const x = Math.round(15 * (age - 20) + 78);
    const y = Math.round(-20 * level + 482 - (20 * ageProportion));
    return { x, y };
}

const EMPTY = {
    pieData: [],
    levels: [0, 0, 0, 0],
    capPotencial: 0,
    dots: [],
    chartDots: [],
    validReplies: [],
};

export default function useDashboardData(filters) {
    const loggedIn = useSelector((state) => state.user.loggedIn);
    const company = useSelector((state) => state.user.company);
    const language = useSelector((state) => state.user.language);

    // El estado se escribe solo desde los callbacks del fetch: `loading` se deriva
    // comparando la petición pedida (requestKey) con la ya resuelta (result.key).
    const [result, setResult] = useState({ key: null, replies: null, error: null });

    const { slidePos, slideArea, slideBU, slideIG, slideAge, slidePotential } = filters;

    const params = useMemo(() => {
        const p = {};
        if (slidePos.length) p.positions = slidePos;
        if (slideArea.length) p.departments = slideArea;
        if (slideIG.length) p.internal_groups = slideIG;
        if (slideBU.length) p.business_units = slideBU;
        return p;
    }, [slidePos, slideArea, slideBU, slideIG]);

    const requestKey = JSON.stringify(params);

    useEffect(() => {
        if (!loggedIn) return undefined;
        const controller = new AbortController();

        getWithParams('/reply', params, { signal: controller.signal })
            .then((res) => setResult({ key: requestKey, replies: res.data, error: null }))
            .catch((err) => {
                if (err?.name === 'AbortError') return;
                console.error('Error al cargar empleados:', err);
                setResult({ key: requestKey, replies: null, error: err });
            });

        return () => controller.abort();
    }, [loggedIn, params, requestKey]);

    const { replies, error } = result;
    const loading = loggedIn && result.key !== requestKey;

    const data = useMemo(() => {
        if (!replies) return EMPTY;
        const tr = withLanguage(language);
        const ruleset = company?.potential_ruleset || DEFAULT_POTENTIAL_RULESET;
        const chartDots = [];
        const dots = [];
        const acum = [0, 0, 0, 0];
        let capPotencial = 0;
        const validReplies = [];

        for (const result of replies) {
            const pot = getJacquesPotential(result, language);
            if (!pot) continue;

            if (slidePotential?.length && !slidePotential.includes(pot.nivelPotencial)) continue;

            const potentialAge = result.age + slideAge;
            let { level, sublevel } = pot;
            let edadInicioRango;
            let edadFinRango;
            ({
                level, sublevel, edadInicioRango, edadFinRango,
            } = projectLevelByAge(
                level, sublevel, slideAge, potentialAge,
                pot.progresionJacques, pot.edadRangoActual,
            ));

            if (level >= 2 && level <= 5) acum[level - 2] += 1;

            const mlLevel = mapToMlLevel(level, sublevel);
            let ageProportion = 0;
            if (edadFinRango > edadInicioRango) {
                ageProportion = (potentialAge - edadInicioRango) / (edadFinRango - edadInicioRango);
            }
            const { x, y } = getCoordsWide2(potentialAge, ageProportion, mlLevel);
            // Domain coords ({age, level}) for the visx chart. Use the chart's own
            // sublevel->level mapping (not mlLevel) so dots land on the drawn grid
            // rows; mapToMlLevel offsets level 1 by a full row (see jacquesGeometry).
            const sublevelLevel = sublevelToDomainLevel(level, sublevel);
            const dotLevel = sublevelLevel == null ? null : sublevelLevel + ageProportion;
            dots.push({ x, y, tip: result.name });

            const altopot = getFuturePotentialCategory(pot.nivelPotencial, pot.tuCurva, ruleset) === 'HIGH';
            if (altopot) capPotencial += 1;
            chartDots.push({
                x, y, age: potentialAge, level: dotLevel, altopot, tip: result.name,
            });

            validReplies.push(result);
        }

        const pieData = acum.map((value, i) => ({ name: `${tr('level')} ${i + 2}`, value }));
        return { pieData, levels: acum, capPotencial, dots, chartDots, validReplies };
    }, [replies, slideAge, slidePotential, language, company]);

    return { loading, error, ...data };
}
